import React, { useState, useCallback, memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../components/Button/Button';
import Notification from '../../../components/Notification/Notification';
import { ConfirmationModal } from '../../../components/shared';
import { useChampionsLeagueData, useTeamManagement, useMatchManagement } from './hooks';
import { LeagueStanding, MatchSelection, CollapsibleSection, } from './components';
import Tabs from '../../../components/Tabs/Tabs';
import TabPanel from '../../../components/TabPanel/TabPanel';
import { KNOCKOUT_MATCHES } from '../../ChampionsLeagueGame/constants';
import { shouldRejectEmptierServerRealResults } from '../../../utils/championsLeagueRealResults';
import './ChampionsLeagueAdminExtended.css';

const ChampionsLeagueAdminExtended = memo(() => {
  const gameData = useSelector((state) => state.game);
  // State for collapsible phases
  const [collapsedPhases, setCollapsedPhases] = useState({
    phase1: true,
    phase2: true,
    phase3: true,
  });
  const [pendingChanges, setPendingChanges] = useState(false);
  
  // Ref to track if we just saved, to prevent useEffect from overwriting our updates
  const justSavedRef = React.useRef(false);

  // Helper function til at få pæne navne for fase keys
  const getPhaseName = (phaseKey) => {
    const phaseNames = {
      'phase0': 'Fase 0 - Ekstra gæt',
      'phase1': 'Fase 1 - Ligastilling',
      'phase2': 'Fase 2 - Knockout (Alle)',
      'phase2Playoff': 'Playoff',
      'phase2Round16': 'Round of 16',
      'phase2Quarters': 'Quarterfinals',
      'phase2Semis': 'Semifinals',
      'phase3': 'Fase 3 - Finalen'
    };
    return phaseNames[phaseKey] || phaseKey;
  };

  // Use custom hooks for data management
  const championsLeagueData = useChampionsLeagueData();
  const teamManagement = useTeamManagement(
    championsLeagueData.allTeams,
    championsLeagueData.updateTeams,
    championsLeagueData.updateRealResults,
    championsLeagueData.setIsLoading,
    championsLeagueData.setSuccess,
    championsLeagueData.setError
  );
  const matchManagement = useMatchManagement(
    championsLeagueData.updateRealResults,
    championsLeagueData.setIsLoading,
    championsLeagueData.setSuccess,
    championsLeagueData.setError
  );

  // Update local state when data comes from backend (only if it's different from current state)
  React.useEffect(() => {
    // Don't update if we just saved (to prevent overwriting our immediate updates)
    if (justSavedRef.current) {
      return;
    }
    
    if (championsLeagueData.currentRealResults) {
      // Only update if data has actually changed to avoid overwriting local updates
      const currentResultsStr = JSON.stringify(matchManagement.realResults);
      const newResultsStr = JSON.stringify(championsLeagueData.currentRealResults);
      
      if (currentResultsStr !== newResultsStr) {
        if (
          shouldRejectEmptierServerRealResults(
            championsLeagueData.currentRealResults,
            matchManagement.realResults
          )
        ) {
          return;
        }

        matchManagement.setRealResults(championsLeagueData.currentRealResults);
      }
    }
  // matchManagement bevidst ikke i deps — undgår loop; læses ved ændring af currentRealResults
  }, [championsLeagueData.currentRealResults]);

  // Toggle collapsible phase
  const togglePhaseCollapse = useCallback((phase) => {
    setCollapsedPhases(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }));
  }, []);

  useEffect(() => {
    if (teamManagement.pendingChanges || matchManagement.pendingChanges) {
      setPendingChanges(true);
    } else {
      setPendingChanges(false);
    }
  }, [teamManagement.pendingChanges, matchManagement.pendingChanges]);

  const handleSaveResults = async () => {
    if (teamManagement.pendingChanges) {
      await teamManagement.savePositionChanges();
    } 
    if (matchManagement.pendingChanges) {
      // Set flag to prevent useEffect from overwriting our immediate updates
      justSavedRef.current = true;
      await matchManagement.saveResults();
      // Keep flag set for longer to prevent refetch from overwriting
      // The state is already updated from response with new references, so UI should update immediately
      setTimeout(() => {
        justSavedRef.current = false;
      }, 3000);
    }
  };
  return (
    <div className="champions-league-admin-extended">
      <div className="admin-page-toolbar">
        <Button
          title="Genberegn point"
          onClick={championsLeagueData.handleRecalculateAllPoints}
          variant="outlined"
          color="primary"
          disabled={championsLeagueData.loading}
          loading={championsLeagueData.recalculatePointsLoading}
        />
        <Button
          title={"Gem"}
          onClick={handleSaveResults}
          color="confirmation"
          disabled={!pendingChanges}
          style={{
            width: '150px',
          }}
          loading={championsLeagueData.loading}
        />
        <Button
          title="Nulstil Spil"
          onClick={championsLeagueData.handleResetGame}
          variant="outlined"
          color="danger"
          disabled={championsLeagueData.loading}
        />
      </div>

      <Notification 
        open={!!championsLeagueData.error}
        onClose={() => championsLeagueData.setError(null)}
        message={championsLeagueData.error}
        type="error"
      />

      <Notification 
        open={!!championsLeagueData.success}
        onClose={() => championsLeagueData.setSuccess(null)}
        message={championsLeagueData.success} 
        type="success" 
      />

      {/* Real Results Management Sections */}
      <div className="admin-content">
        <CollapsibleSection
          title="Fase 1 - Ligastilling (Point Tabel)"
          isCollapsed={collapsedPhases.phase1}
          onToggle={() => togglePhaseCollapse('phase1')}
        >
          <LeagueStanding
            teams={teamManagement.teams}
            teamPoints={teamManagement.teamPoints}
            pendingChanges={teamManagement.pendingChanges}
            draggedTeam={teamManagement.draggedTeam}
            dragOverIndex={teamManagement.dragOverIndex}
            handlePointsChange={teamManagement.handlePointsChange}
            handleDragStart={teamManagement.handleDragStart}
            handleDragEnd={teamManagement.handleDragEnd}
            handleDragOver={teamManagement.handleDragOver}
            handleDragLeave={teamManagement.handleDragLeave}
            handleDrop={teamManagement.handleDrop}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title="Fase 2 - Playoff Kampe"
          isCollapsed={collapsedPhases.phase2}
          onToggle={() => togglePhaseCollapse('phase2')}
        >
          <Tabs>
            <TabPanel tabId={0} label="Playoff Kampe">
            <MatchSelection
              roundType="playoff"
              title="Playoff Kampe"
              numberOfMatches={KNOCKOUT_MATCHES.PLAYOFF}
              teams={teamManagement.teams}
              handleMatchChange={matchManagement.handleMatchChange}
              matches={matchManagement.matches?.playoff || []}
            />
          </TabPanel>
            <TabPanel tabId={1} label="Round of 16">
              <MatchSelection
                roundType="roundOf16"
                title="Round of 16"
                numberOfMatches={KNOCKOUT_MATCHES.EIGHTH_FINALS}
                teams={teamManagement.teams}
                handleMatchChange={matchManagement.handleMatchChange}
                matches={matchManagement.matches?.roundOf16 || []}
              />
            </TabPanel>
            <TabPanel tabId={2} label="Quarterfinals">
              <MatchSelection
                roundType="quarterFinals"
                title="Quarterfinals"
                numberOfMatches={KNOCKOUT_MATCHES.QUARTER_FINALS}
                teams={teamManagement.teams}
                handleMatchChange={matchManagement.handleMatchChange}
                matches={matchManagement.matches?.quarterFinals || []}
              />
            </TabPanel>
            <TabPanel tabId={3} label="Semifinals">
              <MatchSelection
                roundType="semiFinals"
                title="Semifinals"
                numberOfMatches={KNOCKOUT_MATCHES.SEMI_FINALS}
                teams={teamManagement.teams}
                handleMatchChange={matchManagement.handleMatchChange}
                matches={matchManagement.matches?.semiFinals || []}
              />
            </TabPanel>
          </Tabs>
          
        </CollapsibleSection>

        <CollapsibleSection
          title="Fase 3 - Finalen"
          isCollapsed={collapsedPhases.phase3}
          onToggle={() => togglePhaseCollapse('phase3')}
        >
          <MatchSelection
            roundType="final"
            title="Finalen"
            numberOfMatches={KNOCKOUT_MATCHES.FINAL}
            teams={teamManagement.teams}
            handleMatchChange={matchManagement.handleMatchChange}
            matches={matchManagement.matches?.final || []}
          />
        </CollapsibleSection>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={championsLeagueData.phaseConfirmModal.isOpen}
        onClose={() => championsLeagueData.setPhaseConfirmModal({ isOpen: false, phase: '', action: '' })}
        onConfirm={championsLeagueData.confirmTogglePhase}
        title={`${championsLeagueData.phaseConfirmModal.action} fase`}
        message={`Er du sikker på at du vil ${championsLeagueData.phaseConfirmModal.action} ${getPhaseName(championsLeagueData.phaseConfirmModal.phase)}?`}
        confirmText={championsLeagueData.phaseConfirmModal.action}
        cancelText="Annuller"
        confirmButtonType="primary"
      />

      <ConfirmationModal
        isOpen={championsLeagueData.resetConfirmModal.isOpen}
        onClose={() => championsLeagueData.setResetConfirmModal({ isOpen: false })}
        onConfirm={championsLeagueData.confirmResetGame}
        title="Nulstil Champions League spil"
        message="Er du sikker på at du vil nulstille Champions League spillet? Dette vil slette alle spilleres data."
        confirmText="Nulstil"
        cancelText="Annuller"
        confirmButtonType="danger"
      />
    </div>
  );
});

export default ChampionsLeagueAdminExtended;
